import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Facturation.css";

const API = "https://kanban.amservices.eu/api";
const REFRESH_INTERVAL_MS = 30000;

function formatDuration(seconds) {
    const safeSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = safeSeconds % 60;

    return [hours, minutes, remainingSeconds]
        .map(value => String(value).padStart(2, "0"))
        .join(":");
}

function formatDate(value) {
    if (!value) return "—";
    const date = new Date(String(value).replace(" ", "T"));
    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
}

function formatCallAmount(seconds) {
    const amount = (Math.max(0, Number(seconds) || 0) / 3600) * 98;
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(amount);
}

const Facturation = () => {
    const navigate = useNavigate();
    const isAuthenticated = localStorage.getItem("userId") !== null;
    const [factures, setFactures] = useState([]);
    const [appelsResolus, setAppelsResolus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [error, setError] = useState("");

    const fetchBillingData = useCallback(async (silent = false) => {
        const cacheBuster = Date.now();
        const options = { cache: "no-store", credentials: "include" };

        try {
            const [facturesResult, appelsResult] = await Promise.allSettled([
                fetch(`${API}/get_suivis.php?_=${cacheBuster}`, options),
                fetch(`${API}/get_appels_resolus_facturation.php?_=${cacheBuster}`, options)
            ]);
            const errors = [];

            if (facturesResult.status === "fulfilled") {
                const payload = await facturesResult.value.json().catch(() => ({}));
                if (facturesResult.value.ok && payload.success && Array.isArray(payload.data)) {
                    setFactures(payload.data.filter(item => String(item.id_etape) === "400"));
                } else {
                    errors.push(payload.message || "Dossiers de facturation indisponibles");
                }
            } else {
                errors.push("Dossiers de facturation indisponibles");
            }

            if (appelsResult.status === "fulfilled") {
                const payload = await appelsResult.value.json().catch(() => ({}));
                if (appelsResult.value.ok && payload.success && Array.isArray(payload.data)) {
                    setAppelsResolus(payload.data);
                } else {
                    errors.push(payload.message || "Appels résolus indisponibles");
                }
            } else {
                errors.push("Appels résolus indisponibles");
            }

            setError(errors.join(" · "));
        } catch (requestError) {
            console.error("Erreur facturation:", requestError);
            setError("Données de facturation indisponibles");
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    const handleArchive = async (id) => {
        if (!window.confirm("Confirmer le règlement ? Le dossier sera archivé.")) return;

        try {
            const response = await fetch(`${API}/update_etape.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ id, id_etape: "500" })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || payload.success === false) {
                throw new Error(payload.message || "Archivage impossible");
            }
            setFactures(current => current.filter(facture => facture.id !== id));
        } catch (archiveError) {
            console.error(archiveError);
            setError(archiveError.message || "Archivage impossible");
            fetchBillingData(true);
        }
    };

    const handleCashCall = async (appel) => {
        const amount = formatCallAmount(appel.total_seconds);
        if (!window.confirm(`Encaisser l'appel de ${appel.client || "ce client"} pour ${amount} HT ?`)) return;

        try {
            const response = await fetch(`${API}/encaisser_appel.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ appel_id: appel.id })
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok || !payload.success) {
                throw new Error(payload.message || "Encaissement impossible");
            }

            setAppelsResolus(current => current.filter(item => Number(item.id) !== Number(appel.id)));
            setError("");
        } catch (cashError) {
            console.error(cashError);
            setError(cashError.message || "Encaissement impossible");
            fetchBillingData(true);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            navigate("/login");
            return undefined;
        }

        fetchBillingData();
        const timer = window.setInterval(() => fetchBillingData(true), REFRESH_INTERVAL_MS);
        const refreshWhenVisible = () => {
            if (!document.hidden) fetchBillingData(true);
        };

        window.addEventListener("focus", refreshWhenVisible);
        document.addEventListener("visibilitychange", refreshWhenVisible);

        return () => {
            window.clearInterval(timer);
            window.removeEventListener("focus", refreshWhenVisible);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
        };
    }, [fetchBillingData, isAuthenticated, navigate]);

    const lowerSearch = searchTerm.trim().toLowerCase();
    const filteredFactures = factures.filter(facture => {
        if (!lowerSearch) return true;
        return [facture.client_nom, facture.num_devis, facture.num_facture, facture.objet_inter]
            .some(value => String(value || "").toLowerCase().includes(lowerSearch));
    });
    const filteredAppels = appelsResolus.filter(appel => {
        if (!lowerSearch) return true;
        return [appel.client, appel.tel, appel.adresse, appel.note, appel.resolu_par_nom]
            .some(value => String(value || "").toLowerCase().includes(lowerSearch));
    });

    if (loading) return <div className="facturation-page">Chargement @m Services...</div>;

    return (
        <div className="facturation-page">
            <div className="facture-header">
                <button className="btn-back" onClick={() => navigate("/suivi-production")}>
                    ← Retour au Suivi
                </button>

                <div className="header-titles">
                    <h1>Comptabilité & Règlements</h1>
                    <p>{factures.length + appelsResolus.length} élément{factures.length + appelsResolus.length > 1 ? "s" : ""} à facturer</p>
                </div>

                <div className="search-bar-container">
                    <input
                        type="text"
                        placeholder="Rechercher client, devis ou appel..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        className="search-input"
                    />
                </div>
            </div>

            {error && <div className="billing-error">{error}</div>}

            <div className="billing-container">
                <table className="billing-table">
                            <thead>
                                <tr>
                                    <th>Client / Objet</th>
                                    <th>Références</th>
                                    <th>Montant</th>
                                    <th>Échéance</th>
                                    <th className="th-actions">Action / Temps</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredFactures.map(facture => (
                                    <tr key={`facture-${facture.id}`} className="billing-row">
                                        <td className="client-cell">
                                            <div className="client-name">{facture.client_nom}</div>
                                            <div className="objet-sub">{facture.objet_inter}</div>
                                        </td>
                                        <td>
                                            {facture.num_facture && <div className="facture-ref">📄 {facture.num_facture}</div>}
                                            {facture.num_devis && <div className="devis-ref">📝 {facture.num_devis}</div>}
                                        </td>
                                        <td className="amount-cell">
                                            <strong>{parseFloat(facture.montant_chantier || 0).toLocaleString("fr-FR")} €</strong>
                                        </td>
                                        <td>{facture.date_echeance ? new Date(facture.date_echeance).toLocaleDateString("fr-FR") : "—"}</td>
                                        <td className="actions-cell">
                                            <button className="btn-pay" onClick={() => handleArchive(facture.id)}>
                                                💰 Encaisser
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {filteredAppels.map(appel => (
                                    <tr key={`appel-${appel.id}`} className="billing-row call-billing-row">
                                        <td className="client-cell">
                                            <div className="client-name">{appel.client || "Client non renseigné"}</div>
                                            <div className="objet-sub">{appel.adresse || appel.tel || "—"}</div>
                                            {appel.note && <div className="call-note">{appel.note}</div>}
                                        </td>
                                        <td>
                                            <span className="call-source-badge">📞 Appel</span>
                                            <div className="objet-sub">{appel.type === "bureau" ? "Bureau" : "Dépannage"}</div>
                                        </td>
                                        <td className="amount-cell">
                                            <strong className="call-amount">{formatCallAmount(appel.total_seconds)} HT</strong>
                                            <div className="objet-sub">Tarif : 98 € / heure</div>
                                        </td>
                                        <td>
                                            <strong>{formatDate(appel.resolved_at || appel.created_at)}</strong>
                                            <div className="objet-sub">
                                                {appel.resolu_par_nom ? `Par ${appel.resolu_par_nom}` : "Utilisateur non renseigné"}
                                            </div>
                                        </td>
                                        <td className="actions-cell">
                                            <div className="call-billing-actions">
                                                <span className={`call-duration ${Number(appel.total_seconds) === 0 ? "empty" : ""}`}>
                                                    ⏱ {formatDuration(appel.total_seconds)}
                                                </span>
                                                <button className="btn-pay" onClick={() => handleCashCall(appel)}>
                                                    💰 Encaisser
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                {filteredFactures.length === 0 && filteredAppels.length === 0 && (
                    <div className="empty-state"><h3>Aucun élément à facturer</h3></div>
                )}
            </div>
        </div>
    );
};

export default Facturation;
